/* eslint-disable no-param-reassign */
/* eslint-disable prefer-const */
/* eslint-disable no-loop-func */
/* eslint-disable no-continue */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
  IBaseDisplayTicketItem,
  IDisplayTicket,
  IDisplayTicketItem,
} from '../interfaces/IDisplayTicket';
import { OrderTypeEnum } from '../enums/OrderTypeEnum';
import { OrderStatusEnum } from '../enums/OrderStatusEnum';
import { SCHEDULED_FILTERS_BAR_HEIGHT } from '../constants/layout';

// constantes
const fixOffsetWithoutHeader = 93;
const liHeight = 44;
const liAdditionalOrObservationLineHeight = 21;
const fixButtonsHeight = 44;
const fixScheduledBarHeight = 44;
const headerRowHeight = 24;
const paddingTotal = 20;
const fixHeaderRows = headerRowHeight * 2;
// ---------------

interface IBreakTicketsOptions {
  hasScheduledFilters?: boolean;
}

// funções de auxílio

/**
 * @description - Função responsável por verificar quebra de linhas em textos, de acordo com as entradas,
 * para fins de calculo de altura dinãmica do item.
 *
 * @param text - texto a ser verificado
 * @param firstLineHeight - tamanho padrão da linha sem quebra
 * @param extraLineHeight - tamanho de cada quebra de linha
 * @param charBreakpoint - ponto de controle de quebra da linha
 * @param maxExtraRows - limite máximo de linhas extras para o calculo
 * @returns number - altura em px da(s) linha(s)
 */
function checkLineBreak(
  text: string,
  firstLineHeight: number,
  extraLineHeight: number,
  charBreakpoint: number,
  maxExtraRows?: number
): number {
  const { length: strLength } = text;
  if (strLength < charBreakpoint) {
    return firstLineHeight;
  }

  let lines = Math.ceil(strLength / charBreakpoint) - 1 || 1;
  if (maxExtraRows && lines > maxExtraRows) lines = maxExtraRows;

  const extraLinesHeight = lines * extraLineHeight;
  return firstLineHeight + extraLinesHeight;
}

/**
 * @description - Função responsável por obter a altura dinâmica de um item, de acordo com campos que impactam
 * a sua altura final, como observação e adicionais e também quebras de linhas para textos grandes.
 *
 * @todo - verificar uma melhor forma de identificar a quebra de linha com a inclusão do height adicional para cada quebra,
 * hoje verificarmos através de um limite de chars na linha do item, mas há vários fatores que impactam a "limitação"
 * de chars na linha.
 *
 * @param item - item a ser calculado a altur
 * @returns number - altura em px do item
 */
export function calculateTicketItemHeight(
  item: IBaseDisplayTicketItem
): number {
  /** quantidade media de chars que cabem no item de 300px com padding horizontal de 10px considerando também a largura de 5px para indicar a quantidade */
  let breakpoint = 33;
  // se é linha de catalogo, terá sempre o liHeight fixo
  if (item.catalogLine) {
    return liHeight;
  }

  const hasIcon = item.status !== OrderStatusEnum.open || !!item.comboItemOf;
  if (hasIcon) {
    /** desconta 2 chars do breakpoint da linha para considerar a largura do ícone presente no item */
    breakpoint -= 2;
  }

  let itemHeight = checkLineBreak(
    item.name,
    liHeight,
    liAdditionalOrObservationLineHeight,
    breakpoint
  );

  if (item.observations) {
    itemHeight += checkLineBreak(
      item.observations,
      liAdditionalOrObservationLineHeight,
      liAdditionalOrObservationLineHeight,
      33
    );
  }

  if (item.additionals?.length) {
    for (const additional of item.additionals) {
      let max = additional.removable ? 24 : 36;

      if (
        item.additionals.length > 1 &&
        item.additionals[0].id !== additional.id
      ) {
        max -= 5;
      }

      itemHeight += checkLineBreak(additional.name, 21, 21, max);
    }
  }

  return itemHeight;
}

/**
 * @description - Função responsável por obter a altura dinâmica de uma lista de itens pertencentes a um ticket
 *
 * @param items
 * @returns number - altura dos itens em px
 */
function calculateTicketItemsHeight(
  items: IDisplayTicketItem[] | IBaseDisplayTicketItem[]
): number {
  let itemsHeight = 0;

  items.forEach((item) => {
    const itemHeight = calculateTicketItemHeight(item);
    itemsHeight += itemHeight;
  });

  return itemsHeight;
}

/**
 * @description - Função responsável por obter a altura dinâmica do header de um ticket,
 * através de verificações de existências de campos como modo de consumo, tipo de delivery, nome do cliente e etc.
 *
 * @param ticket
 * @returns number - altura em px do header do ticket
 */
function getTicketHeaderDynamicHeight(ticket: IDisplayTicket): number {
  let height = fixButtonsHeight + fixHeaderRows + paddingTotal;

  if (ticket.shippingScheduledTo) {
    height += fixScheduledBarHeight;
  }

  if (ticket.tableName || ticket.tableTicketName) {
    height += headerRowHeight;
  }

  if (
    ticket.type !== OrderTypeEnum.table &&
    ticket.type !== OrderTypeEnum.ticket
  ) {
    if (ticket.consumptionMode || ticket.deliveryType) {
      height += headerRowHeight;
    }
  }

  if (ticket.customerName) {
    height += checkLineBreak(
      ticket.customerName,
      headerRowHeight,
      headerRowHeight,
      22
    );
  }

  if (ticket.observations) {
    height += checkLineBreak(
      ticket.observations,
      headerRowHeight,
      headerRowHeight,
      20,
      3
    );
  }

  if (ticket.licensePlate) {
    height += headerRowHeight;
  }

  if (ticket.operator) {
    height += checkLineBreak(
      ticket.operator,
      headerRowHeight,
      headerRowHeight,
      25
    );
  }

  if (ticket.status === OrderStatusEnum.canceled) {
    height -= fixButtonsHeight;
  }

  return height;
}

/**
 * @description
 * - Função responsável por receber uma lista de itens e retornar uma lista resultante desses itens aninhados
 * com todos os subitens dentro desses itens, a fim de facilitar a manipulação da altura dinâmica do ticket.
 *
 * - os subitens "convertidos" para itens referenciam o item pai através do campo "comboItemOf"
 *
 * @param items
 * @returns IDisplayTicketItem[] - itens + subitens aninhados em uma lista
 */
function transferComboItemsToItemsLevel(
  items: IDisplayTicketItem[]
): IDisplayTicketItem[] {
  const formatted: IDisplayTicketItem[] = [];

  for (const item of items) {
    formatted.push({
      ...item,
      subitems: [],
    });

    const { subitems } = item;
    if ((subitems || []).length > 0) {
      for (const subitem of subitems) {
        formatted.push({
          ...subitem,
          comboItemOf: item.id,
        } as IDisplayTicketItem);
      }
    }
  }

  return formatted;
}

/**
 * @description - Função responsável por reajustar o cursor do eixo Y da altura da tela após o fim
 * da exibição de um ticket, considerando padding, gap e o tamanho do header do próximo ticket (se existente).
 *
 * @param windowHeight number - altura (px) da tela
 * @param yOffset number - posição do cursor de altura do eixo Y atual
 * @param nextTicket IDisplayTicket - próximo ticket da lista
 * @returns number - posição no eixo Y do cursor após o fim do ticket atual
 */
function prepareOffsetYToNextTicket(
  windowHeight: number,
  yOffset: number,
  nextTicket: IDisplayTicket,
  initialYOffset: number
): number {
  let newYOffset = yOffset + 5;
  const availableHeightAtEnd = windowHeight - newYOffset;
  const nextTicketHeaderHeight = getTicketHeaderDynamicHeight(nextTicket);

  if (nextTicketHeaderHeight >= availableHeightAtEnd) {
    newYOffset = initialYOffset;
    return newYOffset;
  }

  if (availableHeightAtEnd < 0) {
    newYOffset = initialYOffset;
  } else if (nextTicket.items.length > 0) {
    const first = nextTicket.items[0];
    let firstHeight = calculateTicketItemsHeight([first]);
    const { id } = first;
    const rest = nextTicket.items.filter((tkt) => tkt.id !== id) || [];
    if (rest.length) {
      firstHeight += liHeight;
    }

    const ticketHeightWithHeaderAndTwoItems =
      nextTicketHeaderHeight + firstHeight;

    if (availableHeightAtEnd < ticketHeightWithHeaderAndTwoItems) {
      newYOffset = initialYOffset;
    }
  }

  return newYOffset;
}

/**
 * @description
 * - Função responsável por efetuar a quebra de um ticket em N tickets de acordo com
 * a quantidade de itens e a altura disponível em tela no ponteiro do eixo Y.
 *
 * @param items IDisplayTicketItem[] | IBaseDisplayTicketItem[] - itens a serem quebrados
 * @param availableHeight number - altura disponível no eixo Y da tela para exibição do ticket
 * @returns
 * - brokenChildren: itens que foram quebrados para exibição em uma continuação
 * - freeItemsOnColumn: itens que couberam na exibição atual do ticket
 */
function splitItems(
  items: IDisplayTicketItem[] | IBaseDisplayTicketItem[],
  availableHeight: number
) {
  const freeItemsOnColumn: IDisplayTicketItem[] = [];
  let brokenChildren: IDisplayTicketItem[] = [];
  let breakIndex: number = null;
  let index = 0;
  let updatedAvailebleHeight = availableHeight;

  while (!breakIndex) {
    const item = items[index];
    if (!item) break;

    const itemHeight = calculateTicketItemHeight(item);
    updatedAvailebleHeight -= itemHeight;
    if (updatedAvailebleHeight <= 0) {
      breakIndex = index;
      break;
    }

    freeItemsOnColumn.push(item as IDisplayTicketItem);
    const next = items[index + 1];
    if (!next) {
      break;
    }

    index++;
  }

  if (breakIndex) {
    brokenChildren = items.slice(breakIndex - 1) as IDisplayTicketItem[];
    freeItemsOnColumn.pop();
  }

  return {
    brokenChildren,
    freeItemsOnColumn,
  };
}
// ---------------

/**
 * @description
 * - Função responsável por executar a quebra dos tickets para as distribuições em tela conforme o scroll horizontal
 * - Visando resolver os problemas que ocorriam na lógica anterior, decidimos por efetuar a quebra dos tickets *ANTES* de sua renderização,
 *   para que na sua renderização não seja necessário nenhum manipulamento de state e etc.
 *
 * @param tickets IDisplayTicket[] - tickets a serem quebrados para renderização
 * @returns IDisplayTicket[] - array resultando de tickets com as quebras
 */
export function breakTickets(
  tickets: IDisplayTicket[],
  { hasScheduledFilters = false }: IBreakTicketsOptions = {}
): IDisplayTicket[] {
  if (!tickets || !tickets.length) {
    return [];
  }

  const mapped = tickets.map((tkt) => ({
    ...tkt,
    items: transferComboItemsToItemsLevel(tkt.items),
  }));

  let brokenTicketsToReturn: IDisplayTicket[] = [];
  const initialYOffset =
    fixOffsetWithoutHeader +
    (hasScheduledFilters ? SCHEDULED_FILTERS_BAR_HEIGHT : 0);
  let yOffset = initialYOffset;
  let windowHeight = document.documentElement.clientHeight;

  const fillTicketBrokenChildren = (
    baseTicket: IDisplayTicket,
    children: IDisplayTicket[],
    brokenChildren: IDisplayTicketItem[],
    nextTicket?: IDisplayTicket
  ): void => {
    yOffset = initialYOffset;
    const availableHeight = windowHeight - yOffset;
    const height = calculateTicketItemsHeight(brokenChildren);
    const itemsHeight = height + liHeight;

    if (itemsHeight > availableHeight) {
      const { freeItemsOnColumn, brokenChildren: _brokenChildren } = splitItems(
        brokenChildren,
        availableHeight
      );

      children.push({
        ...baseTicket,
        items: freeItemsOnColumn,
        hasContinuation: true,
      });

      if (_brokenChildren.length) {
        const _children: IDisplayTicket[] = [];
        fillTicketBrokenChildren(
          baseTicket,
          _children,
          _brokenChildren,
          nextTicket
        );
        const { length } = _children;
        _children.forEach((child, index) => {
          children.push({
            ...child,
            hasContinuation: length > 1 && index + 1 !== length,
            continuationPart: index + 1,
            continuationOf: baseTicket.id,
          });
        });
      } else {
        yOffset += itemsHeight;
        if (nextTicket) {
          yOffset = prepareOffsetYToNextTicket(
            windowHeight,
            yOffset,
            nextTicket,
            initialYOffset
          );
        }
      }
    } else {
      yOffset += itemsHeight;
      children.push({
        ...baseTicket,
        items: brokenChildren,
      });

      if (nextTicket) {
        yOffset = prepareOffsetYToNextTicket(
          windowHeight,
          yOffset,
          nextTicket,
          initialYOffset
        );
      }
    }
  };

  mapped.forEach((ticket, ticketIndex) => {
    const headerHeight = getTicketHeaderDynamicHeight(ticket);
    yOffset += headerHeight;
    const availableHeight = windowHeight - yOffset;
    const itemsHeight = calculateTicketItemsHeight(ticket.items);
    const nextTicket = mapped[ticketIndex + 1];

    if (itemsHeight > availableHeight) {
      let { freeItemsOnColumn, brokenChildren } = splitItems(
        ticket.items,
        availableHeight
      );

      brokenTicketsToReturn.push({
        ...ticket,
        ticketIndex,
        items: freeItemsOnColumn,
        hasContinuation: true,
      });

      if (brokenChildren.length) {
        const children: IDisplayTicket[] = [];
        fillTicketBrokenChildren(ticket, children, brokenChildren, nextTicket);

        const { length } = children;
        children.forEach((child, index) => {
          brokenTicketsToReturn.push({
            ...child,
            ticketIndex,
            hasContinuation: length > 1 && index + 1 !== length,
            continuationPart: index + 1,
            continuationOf: ticket.id,
          });
        });
      } else {
        yOffset += itemsHeight;
        if (nextTicket) {
          yOffset = prepareOffsetYToNextTicket(
            windowHeight,
            yOffset,
            nextTicket,
            initialYOffset
          );
        }
      }
    } else {
      yOffset += itemsHeight;
      brokenTicketsToReturn.push({
        ...ticket,
        ticketIndex,
      });

      if (nextTicket) {
        yOffset = prepareOffsetYToNextTicket(
          windowHeight,
          yOffset,
          nextTicket,
          initialYOffset
        );
      }
    }
  });

  return brokenTicketsToReturn;
}
